import { MIN_PASSWORD_LENGTH } from "@ondo/shared";

/**
 * Copy for the two reset screens.
 *
 * ## ONE FOLDER, TWO SCREENS, which is a deliberate departure from the convention
 *
 * The rule is one folder per screen. These are two halves of one act — ask for a code
 * or a link, then set the password — and they share this file, the model and the
 * stylesheet. Split, there would be two copies of the password rule and two places the
 * sentences could drift, which is the thing the convention exists to prevent.
 *
 * ## THE LENGTH COMES FROM `@ondo/shared`, and that is new
 *
 * It used to be a local `MIN_PASSWORD_LENGTH = 8` here and another in
 * `signup.constants.ts` and a third in the API's schema — three copies of one number,
 * each quoted in a sentence. `domain/password.ts` holds it now, and its header says why.
 *
 * ## AND NOTE WHAT IS NOT HERE: a sentence that varies by whether an account exists
 *
 * `resetPasswordForEmail` answers the same way either way, so there is one sentence and
 * it is written below as a constant rather than taken from a server. That is a change
 * from the rejected design, where an API route returned the sentence so two clients
 * could not word it differently. With Supabase there is no route to return it — so the
 * phone and the website each hold their own copy, and the property that matters is no
 * longer "the same string" but "neither screen branches". Each one's test asserts that
 * about itself.
 */

export const FORGOT_COPY = {
  heading: "Forgotten your password?",
  blurb:
    "Give us the address on your ONDO account and we will send you a link to set a new password.",
  emailLabel: "Email address",
  submit: "Send me a link",
  submitting: "Sending…",
  /**
   * The one sentence this screen says about the address, and it says it always.
   *
   * "If" is doing the work. Supabase deliberately answers identically for a registered
   * and an unregistered address, so this is the whole of what is known — and a form
   * that distinguished would be a test of who holds an ONDO account, which is the
   * register the product keeps closed.
   */
  sent: "If that address has an ONDO account, a link is on its way to it.",
  /**
   * Shown beneath the sentence above, not instead of it.
   *
   * The one honest thing to add: the answer deliberately does not say whether the
   * address is registered, so somebody who mistyped their own would otherwise sit
   * waiting for a mail that is never coming.
   */
  afterwards:
    "Check the address if nothing arrives in a few minutes — we cannot tell you whether an account exists, so an address with a typo in it gets the same answer.",
  /** A wait, not a verdict. The only refusal this door actually produces. */
  throttled:
    "A reset email was sent recently. Wait a little before asking for another.",
  failure: "Could not ask for a reset just now. Try again in a moment.",
  backToSignIn: "Back to sign in",
} as const;

export const RESET_COPY = {
  heading: "Set a new password",
  blurb: "Choose a password you do not use anywhere else.",
  passwordLabel: "New password",
  confirmLabel: "New password again",
  rule: `At least ${String(MIN_PASSWORD_LENGTH)} characters.`,
  submit: "Set my password",
  submitting: "Setting…",
  /**
   * Said BEFORE they commit, not after.
   *
   * It is a consequence somebody may want to prepare for. **And it is not ours to
   * decide**: Supabase revokes the account's other sessions when the password changes.
   * OND-19 asked whether a reset should end them and defaulted to "end them, and say so
   * on the screen"; the identity provider had already answered, so this sentence
   * reports rather than announces.
   */
  willEndSessions:
    "Setting a new password signs this account out everywhere else, so you will need to sign in again on your other devices.",
  mismatch: "Those two do not match.",
  tooShort: `Passwords need at least ${String(MIN_PASSWORD_LENGTH)} characters.`,
  /**
   * The link that carried no recovery session.
   *
   * A different thing from a link the server refused, and collapsing the two would send
   * somebody to the wrong remedy: this one means "you did not arrive here from the
   * email", and an expired one means "ask for a new link". Reached when the address was
   * opened directly, or when the fragment had been consumed already — a second click on
   * the same link, which is OND-118's "tapped twice" case.
   */
  noSession:
    "This page needs the link from your reset email. Open that link again, or ask for a new one — a link can only be used once.",
  askAgain: "Ask for a new link",
  failure: "Could not set that password. The link may have expired — ask for a new one.",
  /** After a successful reset. */
  doneHeading: "Your password is set",
  done: "You are signed in, and this account has been signed out everywhere else.",
  carryOn: "Continue to ONDO",
  /**
   * Shown while the recovery link is being turned into a session, before the form.
   *
   * It is a real wait rather than a flicker: the Supabase client reads the fragment on
   * load and establishes the session asynchronously, so for a moment the page genuinely
   * does not yet know whether it has one. An empty form in that moment would invite
   * somebody to start typing into a screen that was about to re-render.
   */
  opening: "Checking your reset link…",
} as const;
